import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ModalController, ToastController } from "@ionic/angular";
import { TranslocoService } from "@ngneat/transloco";
import { combineLatest } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { ApiService } from "source/app/configuration/services/api.service";
import { ImageTranslationService } from "source/app/configuration/services/image-translation.service";
import { RideOpsUiService } from "source/app/configuration/services/rideops-ui.service";
import { ActionsService } from "source/app/configuration/state/actions.service";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";
import { LocationStateService } from "source/app/configuration/state/location-state.service";
import { NoteTemplateReference, NoteTypeReference } from "source/app/configuration/state/location-state.store";
import { Note } from "source/app/configuration/state/note.model";
import { ImageAttachment } from "source/app/features/checklists/checklist.interface";
import { NoteEditorComponentMode } from "./note-editor.mode";

export { NoteEditorComponentMode };

@Component({
	selector: "app-note-editor",
	templateUrl: "./note-editor.component.html",
	styleUrls: ["./note-editor.component.scss"],
})
export class NoteEditorComponent implements OnInit {
	constructor(
		private modalController: ModalController,
		private formBuilder: FormBuilder,
		private locationStateQuery: LocationStateQuery,
		private locationStateService: LocationStateService,
		private actionsService: ActionsService,
		private imageTranslationService: ImageTranslationService,
		private apiService: ApiService,
		private toastController: ToastController,
		private translocoService: TranslocoService,
		private rideOpsUi: RideOpsUiService,
	) {}

	selectedTemplate: NoteTemplateReference;
	form: FormGroup = this.formBuilder.group({
		title: ["", [Validators.required]],
		description: [""],
		type: [null, [Validators.required]],
		attachments: [[]],
	});

	typeValueChanges$ = this.form.controls["type"].valueChanges.pipe(startWith(null));
	templates$ = this.locationStateQuery.noteTemplates$;

	// Filter out deleted noteTypes unless the current note is of that type
	noteTypes$ = combineLatest([this.locationStateQuery.noteTypes$, this.typeValueChanges$]).pipe(
		map(([noteTypes, typeValue]) => noteTypes.filter((x) => x.isDeleted === false || x.id === typeValue)),
	);

	@Input() mode: NoteEditorComponentMode = NoteEditorComponentMode.Create;
	@Input() noteTemplate: NoteTemplateReference;
	@Input() note: Note;

	modules = {
		toolbar: [
			["bold", "italic", "underline", "strike"],
			[{ list: "ordered" }, { list: "bullet" }],
		],
	};

	isV2 = (): boolean => this.rideOpsUi.isV2Active();

	private getDefaultNoteType = async () => {
		const noteTypes = await this.noteTypes$.firstAsync();

		if (noteTypes.length === 1) {
			return noteTypes[0].id;
		}

		return noteTypes.find((type) => type.isDefault)?.id;
	};
	close = () => {
		this.modalController.dismiss();
	};

	isCreateMode = () => this.mode === NoteEditorComponentMode.Create;

	canAcknowledgeHandover = () =>
		this.mode === NoteEditorComponentMode.ViewAcknowledge &&
		this.note?.isHandover === true &&
		this.note?.acknowledgedByCurrentUser !== true;

	acknowledgeHandover = async () => {
		if (this.canAcknowledgeHandover() !== true) {
			return;
		}

		const success = await this.apiService.acknowledgeNoteHandover(this.note.id);
		if (success !== true) {
			const toast = await this.toastController.create({
				message: this.translocoService.translate("NOTE_EDITOR__ACKNOWLEDGE_FAILED"),
				duration: 4000,
				position: "bottom",
				color: "danger",
			});
			await toast.present();
			return;
		}

		const location = await this.locationStateQuery.location$.firstAsync();
		await this.locationStateService.updateLocationStatus(location.id);
		this.modalController.dismiss({ acknowledged: true });
	};
	save = async () => {
		if (this.form.invalid) {
			return;
		}

		const translatedAttachments = await this.imageTranslationService.translateImages(this.form.value.attachments);

		if (translatedAttachments === null) {
			// Translation failed - images still uploading
			return;
		}

		const rawTitle = this.form.get("title")?.value as string | undefined;
		const titleTrimmed = rawTitle?.trim() ?? "";
		this.actionsService.addCreateNoteAction(
			this.form.value.description,
			this.form.value.type,
			translatedAttachments,
			titleTrimmed,
		);
		this.modalController.dismiss();
	};

	selectTemplate(template: NoteTemplateReference) {
		if (template == this.selectedTemplate) {
			if (!this.isV2()) {
				this.clearTemplate();
			}
			return;
		}

		this.selectedTemplate = template;
		this.form.controls["title"].setValue(template.title ?? template.name);
		this.form.controls["description"].setValue(template.description);
		this.form.controls["type"].setValue(template.noteType);
	}

	clearTemplate() {
		this.selectedTemplate = null;
		this.form.controls["title"].setValue(null);
		this.form.controls["description"].setValue(null);
		this.form.controls["type"].setValue(null);
	}

	typeLabel = (types: NoteTypeReference[]): string | null => {
		const id = this.form.controls["type"].value as string | null;
		return types.find((type) => type.id === id)?.name ?? null;
	};

	selectType = (typeId: string) => {
		this.form.controls["type"].setValue(typeId);
	};

	async ngOnInit() {
		let description = "";
		let type: string = await this.getDefaultNoteType();
		let attachments: ImageAttachment[] = [];
		let title = "";

		if (this.mode == NoteEditorComponentMode.Create) {
			if (this.noteTemplate != undefined) {
				title = this.noteTemplate.title ?? this.noteTemplate.name;
				description = this.noteTemplate.description;
				type = this.noteTemplate.noteType;
			}
		} else {
			description = this.note.description;
			type = this.note.type.id;
			attachments = this.note.attachments;
			title = this.note.title ?? "";
		}

		// setTimeout is necessary to emit the value to the forms valueChanges observable
		setTimeout(() => {
			this.form.controls["title"].setValue(title);
			this.form.controls["description"].setValue(description);
			this.form.controls["type"].setValue(type);
			this.form.controls["attachments"].setValue(attachments);

			if (this.mode === NoteEditorComponentMode.View || this.mode === NoteEditorComponentMode.ViewAcknowledge) {
				this.form.disable();
			}
		});
	}
}
